import { createFileRoute, notFound } from "@tanstack/react-router";
import { getPtBrCalculatorBySlug, getEnCalculatorSlugForPtBrSlug, getPtBrCalculatorSlugForEnSlug } from "@/lib/pt-br-calculators-catalog";
import { getEsCalculatorSlugForEnSlug } from "@/lib/es-calculators-catalog";
import { getDeCalculatorSlugForEnSlug } from "@/lib/de-calculators-catalog";
import { getFrCalculatorSlugForEnSlug } from "@/lib/fr-calculators-catalog";
import { CALCULATOR_CONTENT } from "@/lib/calculator-content";
import { CalculatorShell } from "@/components/calculators/CalculatorShell";
import { CalculatorRenderer } from "@/components/calculators/CalculatorRenderer";

export const Route = createFileRoute("/pt-br/calculators/$slug")({
  loader: ({ params }) => {
    const ptCalc = getPtBrCalculatorBySlug(params.slug);
    if (!ptCalc) throw notFound();
    const enSlug = getEnCalculatorSlugForPtBrSlug(ptCalc.slug);
    const content = CALCULATOR_CONTENT[enSlug];
    if (!content) throw notFound();
    return { ptCalc, enSlug, content };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { ptCalc, enSlug } = loaderData;
    const currentCanonical = `https://picamini.online/pt-br/calculators/${ptCalc.slug}`;
    const enUrl = `https://picamini.online/calculators/${enSlug}`;
    const esUrl = `https://picamini.online/es/calculators/${getEsCalculatorSlugForEnSlug(enSlug)}`;
    const deUrl = `https://picamini.online/de/calculators/${getDeCalculatorSlugForEnSlug(enSlug)}`;
    const frUrl = `https://picamini.online/fr/calculators/${getFrCalculatorSlugForEnSlug(enSlug)}`;

    return {
      meta: [
        { title: `${ptCalc.name} | Picamini.online` },
        { name: "description", content: ptCalc.description },
        { name: "keywords", content: ptCalc.keywords.join(", ") },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: ptCalc.name },
        { property: "og:description", content: ptCalc.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: currentCanonical },
        { property: "og:locale", content: "pt_BR" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${ptCalc.name} — Calculadora Gratuita no Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: ptCalc.name },
        { name: "twitter:description", content: ptCalc.description },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "noindex, follow" },
      ],
      links: [
        { rel: "canonical", href: currentCanonical },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: currentCanonical },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "fr", href: frUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": ptCalc.name,
            "url": currentCanonical,
            "description": ptCalc.description,
            "applicationCategory": "UtilitiesApplication",
            "operatingSystem": "All",
            "inLanguage": "pt-BR",
            "browserRequirements": "Requer JavaScript. Requer HTML5.",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://picamini.online/pt-br/" },
              { "@type": "ListItem", "position": 2, "name": "Calculadoras", "item": "https://picamini.online/pt-br/calculators" },
              { "@type": "ListItem", "position": 3, "name": ptCalc.name, "item": currentCanonical },
            ],
          }),
        },
      ],
    };
  },
  component: SinglePtBrCalculatorPage,
});

function SinglePtBrCalculatorPage() {
  const { ptCalc, enSlug, content } = Route.useLoaderData();

  const calcMetaAdapter = {
    slug: ptCalc.slug,
    name: ptCalc.name,
    tagline: ptCalc.tagline,
    description: ptCalc.description,
    category: ptCalc.category as any,
    icon: ptCalc.icon,
    featured: ptCalc.featured,
    keywords: ptCalc.keywords,
  };

  return (
    <CalculatorShell calc={calcMetaAdapter} content={content} lang="pt-BR">
      <CalculatorRenderer slug={enSlug} />
    </CalculatorShell>
  );
}
